import { useContext, useEffect, useRef } from 'react';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { useNavigate } from 'react-router';
import EmptyMessage from 'components/Dashboard/components/EmptyMessage';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import OutsideFilters from 'components/Dashboard/Filters/ui/OutsideFilters';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { store } from 'components/Store';
import { getUrlParamRaw, decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import InteractionCard, { InteractionListSkeleton, InteractionScrollSkeleton } from './InteractionCard';
import useCrmInteractionsQuery from './useCrmInteractionsQuery';

const CrmInteractions = () => {
  const { addMetaTags } = useAddMetaTags();
  const navigate = useNavigate();
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { interactionLogged } = state;

  const { getSectionFilters } = useGetSectionFilters('CrmInteractions');

  const sectionFilters = getSectionFilters();
  const { hash, filters: compiledFilters } = sectionFilters ?? {};

  const searchQuery = getUrlParamRaw('search') ? decodeUrlQueryValue(getUrlParamRaw('search')) : '';

  const dependencyKey = `${hash}-${searchQuery}`;

  const { interactions, isLoading, isLoadingScroll, thereIsMoreContent, handleScrollBottom, refetch } =
    useCrmInteractionsQuery({
      filters: compiledFilters,
      sort: { field: 'createdAtUtc', order: 'DESC' },
      query: searchQuery,
      dependencyKey,
    });

  useEffect(() => {
    if (interactionLogged) {
      refetch();
      dispatch({ type: 'MODIFY_SECTION', parameter: 'interactionLogged', value: false });
    }
  }, [interactionLogged]);

  useEffect(() => {
    addMetaTags({ title: 'Interactions - People - PolicyMogul', hash: null });
    // Selecting interactions here shows a "Delete interaction(s)" action in the shared selection
    // bar - leaving that selected on navigating away would keep it showing on unrelated list
    // pages (e.g. Monitor inbox), which doesn't make sense there.
    return () => {
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: [] });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: [] });
    };
  }, []);

  useEffect(() => {
    navigate({ search: window.location.search, hash: hash ? `#${hash}` : '' }, { replace: true });
  }, [hash]);

  const filteredInteractions = interactions ?? [];

  return (
    <div className='main-content pr-xl-5 px-md-4 px-3'>
      <div className='row mx-0'>
        <div className='flex-grow-1 reference-main-content'>
          <InteractionListContainer
            isLoading={isLoading}
            interactions={interactions === null ? null : filteredInteractions}
            thereIsMoreContent={thereIsMoreContent}
            isLoadingScroll={isLoadingScroll}
            onScrollBottom={handleScrollBottom}
            dependencyKey={dependencyKey}
          />
        </div>
        <div className='ml-4 outside-filters-container'>
          <OutsideFilters section='CrmInteractions' />
        </div>
      </div>
    </div>
  );
};

const InteractionListContainer = ({
  isLoading,
  interactions,
  thereIsMoreContent,
  onScrollBottom,
  openLogModal,
  dependencyKey,
}) => {
  const [heightContainer, containerRef] = useHeightContainer();
  const scrollbarRef = useRef(null);

  useEffect(() => {
    scrollbarRef.current?.scrollToTop();
  }, [dependencyKey]);

  const handleScroll = ({ scrollTop, scrollHeight, clientHeight }) => {
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1;
    if (scrolledToBottom) {
      onScrollBottom();
    }
  };

  return (
    <div ref={containerRef}>
      <CustomScrollbar
        ref={scrollbarRef}
        className='main-content-scrollbar stakeholder-list-scrollbar'
        style={{ height: `${heightContainer}px` }}
        maximalThumbYSize={100}
        onScroll={handleScroll}
      >
        {isLoading || interactions === null ? (
          <InteractionListSkeleton />
        ) : interactions.length > 0 ? (
          <ul className='policy-list px-0 items-list'>
            {interactions.map((item) => (
              <InteractionCard key={`interaction-${item.id}`} item={item} />
            ))}
            {thereIsMoreContent && <InteractionScrollSkeleton />}
          </ul>
        ) : (
          <EmptyMessage resourceName='interactions' clearContentFilters={true} />
        )}
      </CustomScrollbar>
    </div>
  );
};

export default CrmInteractions;
